import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { finalize, Observable } from 'rxjs';

import { AumicoCoreProvider } from '../../../../aumico-lib-core-fe/src/lib';
import { AnnualReportDataDto } from '../dtos/annual-report-data-dto.interface';
import { ProjectMetaInformationDto } from '../dtos/project-meta-information-dto.interface';
import { ProjectStatus } from '../enums/project-status.enum';
import { ProjectsFilter } from '../interfaces/projects-filter.interface';
import { RoutesUtil } from '../util/RoutesUtil';

@Injectable({
    providedIn: 'root',
})
export class ProjectsService {
    constructor(
        private http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private routesUtil: RoutesUtil,
    ) {}

    getProjectsMetaInformation(
        filter?: ProjectsFilter,
    ): Observable<{ projectsMetaInformation: ProjectMetaInformationDto[]; totalCount: number }> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Projects] Get Projects Meta Information');

        const params = this.routesUtil.buildHttpParams(filter);

        const url = this.routesUtil.getProjectsMetaInformation();

        return this.http
            .get<{ projectsMetaInformation: ProjectMetaInformationDto[]; totalCount: number }>(url, { params })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Projects] Get Projects Meta Information'),
                ),
            );
    }

    getAllProjectsInformationByProjectId(annualReportId: string): Observable<AnnualReportDataDto> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Projects] Get Projects Information By Project Id');

        const url = this.routesUtil.fetchProjectInformationByProjectId(annualReportId);

        return this.http
            .get<AnnualReportDataDto>(url)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Projects] Get Projects Information By Project Id',
                    ),
                ),
            );
    }

    updateProjectMetaInformationStatus(
        projectId: string,
        status: ProjectStatus,
        updateTasks: boolean,
        mandateManagementTaskIds: string[],
        closingSectionIds: string[],
    ): Observable<ProjectMetaInformationDto> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Projects] updateProjectMetaInformationStatus');

        const url = this.routesUtil.updateProjectsMetaInformation(projectId);

        return this.http
            .put<ProjectMetaInformationDto>(url, {
                projectStatus: status,
                updateTasks: updateTasks,
                mandateManagementTaskIds,
                closingSectionIds,
            })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Projects] updateProjectMetaInformationStatus',
                    ),
                ),
            );
    }

    bulkUpdateProjectStatus(projectIds, status, updateTasks, mandateManagementTaskIds, closingSectionIds): Observable<ProjectMetaInformationDto[]> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Projects] bulkUpdateProjectStatus');

        const url = this.routesUtil.bulkUpdateProjectsMetaInformation();

        return this.http
            .put<ProjectMetaInformationDto[]>(url, {
                projectIds,
                projectStatus: status,
                updateTasks: updateTasks,
                mandateManagementTaskIds,
                closingSectionIds,
            })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd(
                        '[Aumico-Projects] bulkUpdateProjectStatus',
                    ),
                ),
            );
    }

    deleteProjectMetaInformation(projectId: string): Observable<void> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Projects] deleteProjectMetaInformation');

        const url = this.routesUtil.deleteProjectsMetaInformation(projectId);

        return this.http
            .delete<void>(url)
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Projects] deleteProjectMetaInformation'),
                ),
            );
    }

    bulkDeleteProjects(
        projectIds: string[],
        forceDeleteWithDependencies?: string[],
    ): Observable<{ deletedIds: string[]; warnings: { projectId: string; dependentReports: string[] }[] }> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Projects] bulkDeleteProjects');

        const url = this.routesUtil.bulkDeleteProjects();
        const body: { projectIds: string[]; forceDeleteWithDependencies?: string[] } = { projectIds };
        if (forceDeleteWithDependencies?.length) {
            body.forceDeleteWithDependencies = forceDeleteWithDependencies;
        }

        return this.http
            .delete<{ deletedIds: string[]; warnings: { projectId: string; dependentReports: string[] }[] }>(url, { body })
            .pipe(
                finalize(() =>
                    this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Projects] bulkDeleteProjects'),
                ),
            );
    }

    fetchProjectIdByAnnualReportMetaInfoId(annualReportMetaId: string): Observable<string> {
        const url = this.routesUtil.fetchProjectIdByAnnualReportMetaInfoId(annualReportMetaId);

        return this.http.get<string>(url);
    }
}
